import * as React from "react";
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { InfoIcon, EyeIcon, EyeOffIcon } from "lucide-react";

import { Button, Dialog, Input, Field } from "@/components/ui";
import { SocialCoachSessionService } from "@/services";
import { textWithLimit } from "@/utils";

interface OwnProps {
  trigger: React.ReactNode;
  userPassword?: string;
  newPassword?: string;
}

type Props = OwnProps;

const changePasswordSchema = z
  .object({
    userPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .refine(val => /(?=.*\d)/.test(val), { message: "Password must contain at least 1 numeric value" })
      .refine(val => /(?=.*[A-Z])/.test(val), { message: "Password must contain at least 1 capital letter" })
      .refine(val => /(?=.*[-+_!@#$%^&*.,?])/.test(val), {
        message: "Password must contain at least 1 special character",
      }),
    confirmPassword: z.string().min(1, "Confirm password is required"),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

export const UserChangePasswordForm: React.FC<Props> = ({ trigger, userPassword = "", newPassword = "" }) => {
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onTouched",
    defaultValues: {
      userPassword,
      newPassword,
      confirmPassword: "",
    },
  });

  // Reset form when modal open state changes
  React.useEffect(() => {
    if (open) {
      form.reset();
      setSuccessMessage(undefined);
      setErrorMessage(undefined);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open, form]);

  const onSubmit = async (data: ChangePasswordFormValues) => {
    setLoading(true);
    setSuccessMessage(undefined);
    setErrorMessage(undefined);
    try {
      await SocialCoachSessionService.userChangePassword(data.userPassword, data.newPassword);
      setSuccessMessage("Password changed successfully");
      toast.success("Password changed successfully");
    } catch (e: any) {
      const errorMsg = textWithLimit(e?.message || e?.toString() || "Unknown error", 80);
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog trigger={trigger} open={open} onOpenChange={setOpen} size="sm" showCloseButton={true}>
      <Dialog.Header>
        <Dialog.Title>Change Password</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>
        <form id="change-password-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Field.Group>
            <Controller
              name="userPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Field.Label>Current Password</Field.Label>
                  <Input {...field} type="password" disabled={loading} placeholder={"..."} />
                  {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                </Field>
              )}
            />

            <Controller
              name="newPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <div className="flex items-center justify-between">
                    <Field.Label>New Password</Field.Label>
                    <div className="group relative cursor-help text-slate-400 hover:text-slate-600 transition-colors">
                      <InfoIcon className="size-4" />
                      <div className="absolute right-0 top-6 hidden group-hover:block w-72 bg-white border border-slate-200 shadow-md rounded-md p-3 text-xs text-slate-600 z-50 leading-relaxed">
                        <p className="font-semibold mb-1 text-slate-800">Requirements:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Password must be at least 8 characters.</li>
                          <li>Password must contain at least 1 capital letter.</li>
                          <li>Password must contain at least 1 numeric value.</li>
                          <li>Password must contain at least 1 special character.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center w-full">
                    <Input
                      {...field}
                      type={showNewPassword ? "text" : "password"}
                      disabled={loading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(prev => !prev)}
                      className="absolute right-3 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                    >
                      {showNewPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                    </button>
                  </div>
                  {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                </Field>
              )}
            />

            <Controller
              name="confirmPassword"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Field.Label>Confirm New Password</Field.Label>
                  <div className="relative flex items-center w-full">
                    <Input
                      {...field}
                      type={showConfirmPassword ? "text" : "password"}
                      disabled={loading}
                      placeholder="Confirm New Password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      className="absolute right-3 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                    >
                      {showConfirmPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                    </button>
                  </div>
                  {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </Field.Group>

          {errorMessage && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-xs font-medium">{errorMessage}</div>
          )}

          {successMessage && (
            <div className="p-3 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg text-xs font-medium">
              {successMessage}
            </div>
          )}
        </form>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" form="change-password-form" disabled={loading} loading={loading}>
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
